import { useEffect, useState } from "react";
import {
  Aviso,
  Botao,
  Cabecalho,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Tabela,
} from "../componentes/ui";
import { ConfirmacaoDeTrocaDePerfil } from "../componentes/acesso/ConfirmacaoDeTrocaDePerfil";
import { ConviteDePessoa } from "../componentes/acesso/ConviteDePessoa";
import { TelasDaPessoa } from "../componentes/acesso/TelasDaPessoa";
import { useCatalogoDePermissoesDosUsuarios } from "../nucleo/acesso";
import { mensagemDoConsole } from "../nucleo/console";
import { useFocoDaConfirmacao } from "../nucleo/foco";
import { GRUPOS, PERMISSOES_DO_CONSOLE } from "../nucleo/permissoesDoConsole";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { resumoDaPessoa, useMatrizDeTelas, type MatrizDeTelas, type PessoaComTelas } from "../nucleo/telas";
import { useTitulo } from "../nucleo/titulo";
import {
  useAlterarAtivacao,
  useAlterarPerfil,
  useCancelarConvite,
  useConvitesDaConta,
  useVincularGeoTask,
  useDesvincularGeoTask,
  useDefinirExcecoes,
  useExcecoesDoUsuario,
} from "../nucleo/usuarios";

function dataHora(valor: string | null): string {
  if (!valor) return "nunca";
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(data);
}

/** O texto de um status que muda a cada tecla, entregue ao leitor de tela só quando para de mudar. */
function useAnuncioAtrasado(texto: string, atraso = 700): string {
  const [anunciado, setAnunciado] = useState(texto);
  useEffect(() => {
    const espera = setTimeout(() => setAnunciado(texto), atraso);
    return () => clearTimeout(espera);
  }, [texto, atraso]);
  return anunciado;
}

/**
 * Usuários internos: quem da GEOvendas entra no console, com que perfil e o que cada pessoa
 * alcança, tela a tela. Duas permissões são sensíveis e vêm marcadas: liberar ambiente (inicia o
 * relógio contratual e não se desfaz) e revelar credencial do cofre (exclusiva da infraestrutura).
 * O servidor recusa deixar a equipe sem ninguém que possa liberar ambiente; a recusa aparece aqui
 * com a frase dele.
 */
export function UsuariosInternos() {
  useTitulo("Usuários internos");
  const sessao = useSessao();

  if (!podeNaSessao(sessao.data, "usuarios:gerenciar")) {
    return (
      <Aviso tom="marca" data-testid="usuarios-internos-sem-permissao">
        A administração da equipe interna fica com quem tem a permissão de gerenciar usuários.
      </Aviso>
    );
  }

  return <EquipeEConvites />;
}

function EquipeEConvites() {
  const matriz = useMatrizDeTelas("interno");
  const perfis = (matriz.data?.perfis ?? []).map((perfil) => ({
    valor: perfil.chave,
    rotulo: perfil.nome,
  }));

  return (
    <div data-testid="usuarios-internos">
      <h1 className="titulo-pagina">Usuários internos</h1>
      <p className="lead">Quem da GEOvendas entra no console, e o que cada pessoa alcança.</p>
      <Aviso tom="atencao" className="mb-6" data-testid="aviso-liberar-ambiente">
        <b>Liberar ambiente é a permissão mais sensível do console.</b> É o ato que inicia o relógio
        contratual e não se desfaz. O sistema recusa deixar a equipe sem ninguém que possa liberar.
      </Aviso>
      {matriz.isError ? <Aviso tom="erro">{mensagemDoConsole(matriz.error)} <Botao tamanho="pequeno" variante="fantasma" onClick={() => void matriz.refetch()}>Tentar de novo</Botao></Aviso> : null}
      {perfis.length > 0 ? (
        <ConviteDePessoa perfis={perfis} perfilInicial="" matriz={matriz.data} />
      ) : null}
      {matriz.isPending ? <p role="status">Carregando a equipe…</p> : null}
      {matriz.data ? <EquipeInterna matriz={matriz.data} perfis={perfis} /> : null}
      <ConvitesDaEquipe />
    </div>
  );
}

function EquipeInterna({
  matriz,
  perfis,
}: {
  matriz: NonNullable<ReturnType<typeof useMatrizDeTelas>["data"]>;
  perfis: readonly { valor: string; rotulo: string }[];
}) {
  const alterarPerfil = useAlterarPerfil();
  const alterarAtivacao = useAlterarAtivacao();
  const vincular = useVincularGeoTask();
  const desvincular = useDesvincularGeoTask();
  // O catálogo marca o que é sensível na confirmação de troca de perfil: já vem com a lista.
  useCatalogoDePermissoesDosUsuarios();
  const [aberta, setAberta] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  const [perfil, setPerfil] = useState("");
  const [situacao, setSituacao] = useState("ativos");
  const [emailGeoTask, setEmailGeoTask] = useState("");
  const [trocaPendente, setTrocaPendente] = useState<{ pessoa: PessoaComTelas; papel: string; origem: HTMLElement } | null>(null);
  const [confirmandoGeoTask, setConfirmandoGeoTask] = useState<{ acao: "trocar" | "desvincular"; origem: HTMLElement } | null>(null);
  const confirmacaoDoGeoTask = useFocoDaConfirmacao<HTMLDivElement>(confirmandoGeoTask?.acao ?? null, confirmandoGeoTask?.origem ?? null);
  // Cada ação mostra só o próprio resultado: um erro antigo não pode esconder o novo, nem o
  // "vínculo confirmado" de uma pessoa ficar na tela quando outra é aberta. Uma troca de perfil
  // ainda por confirmar também sai: a pergunta não pode sobreviver à pessoa ou ao filtro.
  function limparResultados() {
    alterarPerfil.reset();
    alterarAtivacao.reset();
    vincular.reset();
    desvincular.reset();
    setTrocaPendente(null);
  }
  function trocarPerfil(pessoa: PessoaComTelas, papel: string, origem: HTMLElement) {
    if (alterarPerfil.isPending) return;
    limparResultados();
    if (pessoa.ajustes.length > 0) {
      setTrocaPendente({ pessoa, papel, origem });
      return;
    }
    alterarPerfil.mutate({ usuarioId: pessoa.usuario.id, papel });
  }
  const pessoaAberta = matriz.pessoas.find((pessoa) => pessoa.usuario.id === aberta);
  const rotuloDoPerfil = (chave: string) =>
    perfis.find((perfil) => perfil.valor === chave)?.rotulo ?? chave;
  const radares = [
    ["radar:integracao", "Integração"],
    ["radar:onboarding", "Onboarding"],
    ["radar:infra", "Infra"],
  ] as const;
  const radaresDoPerfil = (pessoa: PessoaComTelas) => new Set(
    matriz.perfis.filter((item) => pessoa.usuario.papeis.includes(item.chave))
      .flatMap((item) => item.permissoes),
  );
  const semVinculo = matriz.pessoas.filter((pessoa) =>
    pessoa.usuario.ativo && !pessoa.usuario.geotask_id &&
    radares.some(([chave]) => radaresDoPerfil(pessoa).has(chave))
  );
  const filtradas = matriz.pessoas.filter(({ usuario }) => {
    const texto = busca.toLocaleLowerCase("pt-BR").trim();
    return (!texto || `${usuario.nome} ${usuario.email}`.toLocaleLowerCase("pt-BR").includes(texto)) &&
      (!perfil || usuario.papeis.includes(perfil)) &&
      (situacao === "todos" || usuario.ativo === (situacao === "ativos"));
  });
  const anuncioDoFiltro = useAnuncioAtrasado(`Resultado da busca: ${filtradas.length} pessoa(s).`);

  return (
    <section className="mb-6" aria-labelledby="titulo-equipe-interna">
      <h2 className="titulo-secao" id="titulo-equipe-interna">Equipe</h2>
      {semVinculo.length > 0 ? (
        <Aviso tom="atencao" className="mb-3" data-testid="aviso-sem-geotask">
          {semVinculo.map((pessoa) => pessoa.usuario.nome).join(", ")} com radar e sem vínculo com o
          GEOtask: o radar pessoal fica vazio até o vínculo ser feito.
        </Aviso>
      ) : null}
      <div className="mb-3 flex flex-wrap gap-2">
        <label>Buscar por nome ou e-mail
          <input className="campo__controle" value={busca} onChange={(evento) => { setTrocaPendente(null); setBusca(evento.target.value); }} />
        </label>
        <label>Perfil
          <select className="campo__controle" value={perfil} onChange={(evento) => { setTrocaPendente(null); setPerfil(evento.target.value); }}>
            <option value="">Todos os perfis</option>
            {perfis.map((item) => <option key={item.valor} value={item.valor}>{item.rotulo}</option>)}
          </select>
        </label>
        <label>Situação
          <select className="campo__controle" value={situacao} onChange={(evento) => { setTrocaPendente(null); setSituacao(evento.target.value); }}>
            <option value="ativos">Ativos</option>
            <option value="inativos">Desativados</option>
            <option value="todos">Todos</option>
          </select>
        </label>
      </div>
      <p className="celula-secundaria">{filtradas.length} pessoa(s) com estes filtros.</p>
      <p role="status" className="sr-only">{anuncioDoFiltro}</p>
      <Tabela legenda="Equipe interna, seus perfis e o último acesso.">
        <Cabecalho>
          <Linha>
            <CelulaCabecalho>Pessoa</CelulaCabecalho>
            <CelulaCabecalho>Perfil</CelulaCabecalho>
            <CelulaCabecalho>Radares</CelulaCabecalho>
            <CelulaCabecalho>GEOtask</CelulaCabecalho>
            <CelulaCabecalho>Último acesso</CelulaCabecalho>
            <CelulaCabecalho>Situação</CelulaCabecalho>
            <CelulaCabecalho>Ações</CelulaCabecalho>
          </Linha>
        </Cabecalho>
        <Corpo>
          {filtradas.map((pessoa) => {
            const { usuario, ajustadas } = pessoa;
            const radaresDeAtribuicao = radaresDoPerfil(pessoa);
            return (
            <Linha key={usuario.id} data-testid={`interno-${usuario.id}`}>
              <Celula>
                <b>{usuario.nome}</b>
                {!usuario.ativo ? (
                  <>
                    {" "}
                    <Chip estado="neutro" semPonto>inativo</Chip>
                  </>
                ) : null}
                <div className="celula-secundaria">
                  {usuario.email}
                  {usuario.cargo ? ` · ${usuario.cargo}` : ""}
                  {usuario.telefone ? ` · ${usuario.telefone}` : ""}
                </div>
              </Celula>
              <Celula>
                <select
                  className="campo__controle"
                  aria-label={`Perfil de ${usuario.nome}`}
                  value={usuario.papeis[0] ?? ""}
                  disabled={!usuario.ativo}
                  onChange={(evento) => trocarPerfil(pessoa, evento.target.value, evento.currentTarget)}
                >
                  {usuario.papeis[0] ? null : <option value="">Sem perfil</option>}
                  {perfis.map((perfil) => (
                    <option key={perfil.valor} value={perfil.valor}>
                      {perfil.rotulo}
                    </option>
                  ))}
                </select>
                {ajustadas.length > 0 ? <div className="celula-secundaria">{ajustadas.length} ajuste(s) individual(is)</div> : null}
              </Celula>
              <Celula className="celula-secundaria">
                {radares.filter(([chave]) => radaresDeAtribuicao.has(chave)).map(([, rotulo]) => rotulo).join(", ") || "—"}
                {radaresDeAtribuicao.has("radar:equipe") ? <div className="celula-secundaria">Vê a equipe</div> : null}
                <div className="celula-secundaria">{resumoDaPessoa(matriz, usuario.id)} telas</div>
              </Celula>
              <Celula>
                {usuario.geotask_id ? <Chip estado="ok" semPonto>Vinculado</Chip> : <Chip estado="atencao" semPonto>Sem vínculo</Chip>}
              </Celula>
              <Celula className="celula-secundaria">{dataHora(usuario.ultimo_acesso_em)}</Celula>
              <Celula>{usuario.ativo ? "Ativo" : "Desativado"}</Celula>
              <Celula>
                <Botao
                  tamanho="pequeno"
                  variante="fantasma"
                  onClick={() => {
                    setEmailGeoTask("");
                    setConfirmandoGeoTask(null);
                    limparResultados();
                    setAberta(aberta === usuario.id ? null : usuario.id);
                  }}
                  aria-label={`Permissões de ${usuario.nome}`}
                  data-testid={`permissoes-${usuario.id}`}
                >
                  Permissões
                </Botao>{" "}
                <Botao
                  tamanho="pequeno"
                  variante="fantasma"
                  disabled={alterarAtivacao.isPending}
                  onClick={() => {
                    limparResultados();
                    alterarAtivacao.mutate({ usuarioId: usuario.id, ativo: !usuario.ativo });
                  }}
                >
                  {usuario.ativo ? "Desativar" : "Reativar"}
                </Botao>
              </Celula>
            </Linha>
          );})}
          {filtradas.length === 0 ? <Linha><Celula colSpan={7}>Ninguém com esses filtros. Ajuste a busca ou limpe os filtros.</Celula></Linha> : null}
        </Corpo>
      </Tabela>
      {trocaPendente ? (
        <ConfirmacaoDeTrocaDePerfil
          pessoa={trocaPendente.pessoa}
          papel={trocaPendente.papel}
          origem={trocaPendente.origem}
          matriz={matriz}
          nomeDoPerfil={rotuloDoPerfil}
          enviando={alterarPerfil.isPending}
          aoCancelar={() => setTrocaPendente(null)}
          aoConfirmar={() => {
            alterarPerfil.mutate({ usuarioId: trocaPendente.pessoa.usuario.id, papel: trocaPendente.papel });
            setTrocaPendente(null);
          }}
        />
      ) : null}
      {alterarPerfil.isError || alterarAtivacao.isError ? (
        <Aviso tom="erro" className="mt-3" data-testid="erro-da-equipe">
          {mensagemDoConsole(alterarPerfil.error ?? alterarAtivacao.error)}
        </Aviso>
      ) : null}
      {pessoaAberta ? (
        <>
        <div className="mb-3 mt-3">
          <h3>Vínculo com o GEOtask de {pessoaAberta.usuario.nome}</h3>
          <p>{pessoaAberta.usuario.geotask_id
            ? `Identificador: ${pessoaAberta.usuario.geotask_id}`
            : radares.some(([chave]) => radaresDoPerfil(pessoaAberta).has(chave))
              ? "Sem vínculo. O radar pessoal ficará vazio."
              : "Sem vínculo com o GEOtask."}</p>
          <label>E-mail para buscar no GEOtask
            <input className="campo__controle" type="email" value={emailGeoTask} placeholder={pessoaAberta.usuario.email} onChange={(evento) => setEmailGeoTask(evento.target.value)} />
          </label>
          <div className="mt-2 flex gap-2">
            <Botao
              disabled={vincular.isPending}
              onClick={(evento) => {
                limparResultados();
                if (pessoaAberta.usuario.geotask_id) setConfirmandoGeoTask({ acao: "trocar", origem: evento.currentTarget });
                else vincular.mutate({ usuarioId: pessoaAberta.usuario.id, email: emailGeoTask || pessoaAberta.usuario.email });
              }}
            >
              {vincular.isPending ? "Buscando…" : pessoaAberta.usuario.geotask_id ? "Trocar vínculo" : "Buscar pelo e-mail"}
            </Botao>
            {pessoaAberta.usuario.geotask_id ? (
              <Botao
                variante="fantasma"
                disabled={desvincular.isPending}
                onClick={(evento) => {
                  limparResultados();
                  setConfirmandoGeoTask({ acao: "desvincular", origem: evento.currentTarget });
                }}
              >
                Desvincular
              </Botao>
            ) : null}
          </div>
          {confirmandoGeoTask && pessoaAberta.usuario.geotask_id ? (
            <Aviso tom="atencao" className="mt-3" data-testid="confirmar-geotask">
              <div
                ref={confirmacaoDoGeoTask}
                tabIndex={-1}
                role="group"
                aria-labelledby="pergunta-do-geotask"
                onKeyDown={(evento) => {
                  if (evento.key === "Escape") setConfirmandoGeoTask(null);
                }}
              >
                <p id="pergunta-do-geotask">
                  {confirmandoGeoTask.acao === "trocar"
                    ? `Trocar o vínculo de ${pessoaAberta.usuario.nome}? O identificador ${pessoaAberta.usuario.geotask_id} será substituído pelo usuário do GEOtask com o e-mail ${emailGeoTask || pessoaAberta.usuario.email}.`
                    : `Desvincular ${pessoaAberta.usuario.nome} do GEOtask? O radar pessoal fica vazio até um novo vínculo.`}
                </p>
                <Botao tamanho="pequeno" variante="fantasma" onClick={() => setConfirmandoGeoTask(null)}>Cancelar</Botao>{" "}
                <Botao
                  tamanho="pequeno"
                  onClick={() => {
                    const usuarioId = pessoaAberta.usuario.id;
                    if (confirmandoGeoTask.acao === "trocar") vincular.mutate({ usuarioId, email: emailGeoTask || pessoaAberta.usuario.email });
                    else desvincular.mutate(usuarioId);
                    setConfirmandoGeoTask(null);
                  }}
                >
                  {confirmandoGeoTask.acao === "trocar" ? "Confirmar troca do vínculo" : "Confirmar desvínculo"}
                </Botao>
              </div>
            </Aviso>
          ) : null}
          {vincular.isError || desvincular.isError ? (
            <Aviso tom="erro" className="mt-3" data-testid="erro-do-geotask">
              {mensagemDoConsole(vincular.error ?? desvincular.error)}
            </Aviso>
          ) : null}
          {vincular.isSuccess ? <p role="status">Vínculo de {pessoaAberta.usuario.nome} com o GEOtask confirmado.</p> : null}
          {desvincular.isSuccess ? <p role="status">Vínculo de {pessoaAberta.usuario.nome} com o GEOtask removido.</p> : null}
        </div>
        <TelasDaPessoa
          key={pessoaAberta.usuario.id}
          pessoa={pessoaAberta}
          matriz={matriz}
          nomeDoPerfil={rotuloDoPerfil}
        />
        <PermissoesDaPessoa key={`granular-${pessoaAberta.usuario.id}`} pessoa={pessoaAberta} matriz={matriz} />
        </>
      ) : null}
    </section>
  );
}

function PermissoesDaPessoa({ pessoa, matriz }: { pessoa: PessoaComTelas; matriz: MatrizDeTelas }) {
  const excecoes = useExcecoesDoUsuario(pessoa.usuario.id);
  const catalogo = useCatalogoDePermissoesDosUsuarios();
  const definir = useDefinirExcecoes();
  const permissoesDosPerfis = new Set(
    matriz.perfis.filter((item) => pessoa.usuario.papeis.includes(item.chave))
      .flatMap((item) => item.permissoes),
  );
  const disponiveis = new Set(
    (catalogo.data ?? []).map((item) => item.chave),
  );
  const agrupadas = GRUPOS.map((grupo) => ({
    grupo,
    itens: PERMISSOES_DO_CONSOLE.filter(([chave, area]) => area === grupo && disponiveis.has(chave)),
  })).filter((item) => item.itens.length > 0);

  function ajustar(chave: string, ligado: boolean) {
    const base = permissoesDosPerfis.has(chave);
    const outros = (excecoes.data ?? []).filter((item) => item.permissao_chave !== chave)
      .map(({ permissao_chave, efeito, motivo }) => ({ permissao_chave, efeito, motivo }));
    if (ligado !== base) {
      outros.push({ permissao_chave: chave, efeito: ligado ? "grant" : "revoke", motivo: "Ajuste individual da equipe" });
    }
    definir.mutate({ usuarioId: pessoa.usuario.id, excecoes: outros });
  }

  return <section className="mt-4" aria-label={`Permissões individuais de ${pessoa.usuario.nome}`}>
    <h3>Permissões por área</h3>
    <p className="celula-secundaria">Ajustes individuais continuam quando o perfil muda. Eles dão acesso à tela, mas não colocam a pessoa na equipe de atribuição dos radares.</p>
    {excecoes.isPending ? <p role="status">Carregando ajustes individuais…</p> : null}
    {excecoes.isError ? <Aviso tom="erro">{mensagemDoConsole(excecoes.error)} <Botao tamanho="pequeno" variante="fantasma" onClick={() => void excecoes.refetch()}>Tentar de novo</Botao></Aviso> : null}
    {catalogo.isPending ? <p role="status">Carregando permissões disponíveis…</p> : null}
    {catalogo.isError ? <Aviso tom="erro">{mensagemDoConsole(catalogo.error)} <Botao tamanho="pequeno" variante="fantasma" onClick={() => void catalogo.refetch()}>Tentar de novo</Botao></Aviso> : null}
    {definir.isError ? <Aviso tom="erro">{mensagemDoConsole(definir.error)}</Aviso> : null}
    <div className="grid gap-3 md:grid-cols-2">
      {agrupadas.map(({ grupo, itens }) => <fieldset key={grupo}>
        <legend className="campo__rotulo">{grupo}</legend>
        {itens.map(([chave, , rotulo]) => {
          const noPerfil = permissoesDosPerfis.has(chave);
          const efetiva = pessoa.permissoes.includes(chave);
          const ajustada = efetiva !== noPerfil;
          return <label key={chave} className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={efetiva}
              disabled={!excecoes.data || definir.isPending || (chave === "credencial:revelar" && !noPerfil)}
              onChange={(evento) => ajustar(chave, evento.target.checked)}
              data-testid={`ajuste-${chave}`} />
            <span>{rotulo} <small className="celula-secundaria">{chave === "credencial:revelar" && !noPerfil ? "exclusiva do perfil Infraestrutura" : ajustada ? (efetiva ? "só para esta pessoa" : "retirado só aqui") : "do perfil"}</small></span>
          </label>;
        })}
      </fieldset>)}
    </div>
  </section>;
}

function ConvitesDaEquipe() {
  const convites = useConvitesDaConta();
  const cancelar = useCancelarConvite();
  if (convites.isPending) return <p role="status">Carregando convites pendentes…</p>;
  if (convites.isError) return <Aviso tom="erro">A equipe foi carregada, mas os convites não. {mensagemDoConsole(convites.error)} <Botao tamanho="pequeno" variante="fantasma" onClick={() => void convites.refetch()}>Tentar de novo</Botao></Aviso>;
  const pendentes = (convites.data ?? []).filter((convite) => convite.organizacao_id === null);
  if (pendentes.length === 0) return null;

  return (
    <section className="mb-6" aria-labelledby="titulo-convites-da-equipe">
      <h2 className="titulo-secao" id="titulo-convites-da-equipe">Convites pendentes</h2>
      <Tabela legenda="Convites da equipe que ainda podem ser aceitos.">
        <Cabecalho>
          <Linha>
            <CelulaCabecalho>E-mail</CelulaCabecalho>
            <CelulaCabecalho>Perfil</CelulaCabecalho>
            <CelulaCabecalho>Expira</CelulaCabecalho>
            <CelulaCabecalho>Ação</CelulaCabecalho>
          </Linha>
        </Cabecalho>
        <Corpo>
          {pendentes.map((convite) => (
            <Linha key={convite.id} data-testid={`convite-${convite.id}`}>
              <Celula>{convite.email}</Celula>
              <Celula>{convite.papel_chave}</Celula>
              <Celula>{dataHora(convite.expira_em)}</Celula>
              <Celula>
                <Botao
                  tamanho="pequeno"
                  variante="fantasma"
                  disabled={cancelar.isPending}
                  onClick={() => cancelar.mutate(convite.id)}
                >
                  Cancelar
                </Botao>
              </Celula>
            </Linha>
          ))}
        </Corpo>
      </Tabela>
    </section>
  );
}
